import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDisclosure } from "@mantine/hooks";
import {
  useForm,
  isNotEmpty,
  isEmail,
  matchesField,
  UseFormReturnType,
} from "@mantine/form";
import { SubscriptionApi } from "@/api/subscription.api";
import { useMe } from "@/context/MeContext";
import { OrderApi } from "@/api/order.api";
import { UserApi } from "@/api/user.api";
import { findActiveSubscription } from "@/helpers/issuesAndSubscriptions";
import {
  successNotification,
  errorNotification,
} from "@/helpers/notifications";
import AreYouSureModal from "@/components/Shared/AreYouSureModal/AreYouSureModal";
import MobileView from "@/components/User/Profile/MobileView/MobileView";
import PersonalData from "@/components/User/Profile/Steps/PersonalData";
import Subscription from "@/components/User/Profile/Steps/Subscription";
import Password from "@/components/User/Profile/Steps/Password";
import History from "@/components/User/Profile/Steps/History";
import "./Profile.css";
import {
  UserSubscription,
  Subscription as SubscriptionType,
  Order,
} from "@/api/types";

export type PersonalDataFormType = UseFormReturnType<{
  firstName: string;
  lastName: string;
  email: string;
}>;

export type PasswordFormType = UseFormReturnType<{
  currentPassword: string;
  newPassword: string;
  repeatPassword: string;
}>;

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const { me, logout, updateMe } = useMe();
  const [mobileStep, setMobileStep] = useState("");
  const [activeSubscription, setActiveSubscription] =
    useState<UserSubscription | null>();
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionType>();
  const [orders, setOrders] = useState<Order[]>();
  const [opened, { open, close }] = useDisclosure(false);

  const getMySubscriptionData = async () => {
    const subData = await SubscriptionApi.getSubscriptionData();
    if (subData) setSubscriptionData(subData);
    const subscriptions = await UserApi.getMySubscriptionData();
    if (subscriptions)
      setActiveSubscription(findActiveSubscription(subscriptions));
  };

  const getMyOrders = async () => {
    const orderData = await OrderApi.getMyOrders();
    if (orderData) {
      setOrders(orderData);
    }
  };

  useEffect(() => {
    getMySubscriptionData();
    getMyOrders();
  }, []);

  const logoutHandler = async () => {
    await logout();
    navigate("/login");
  };

  const personalDataForm = useForm({
    mode: "uncontrolled",
    initialValues: {
      firstName: me?.firstName || "",
      lastName: me?.lastName || "",
      email: me?.email || "",
    },
    validate: {
      firstName: isNotEmpty("Ime je obavezno"),
      lastName: isNotEmpty("Prezime je obavezno"),
      email: isEmail("E-mail nije ispravan"),
    },
  });

  const passwordForm = useForm({
    mode: "uncontrolled",
    initialValues: {
      currentPassword: "",
      newPassword: "",
      repeatPassword: "",
    },
    validate: {
      currentPassword: isNotEmpty("Obavezno polje"),
      newPassword: isNotEmpty("Obavezno polje"),
      repeatPassword: matchesField("newPassword", "Lozinke se ne podudaraju"),
    },
  });

  const saveMyDetails = async (values: typeof personalDataForm.values) => {
    const result = await UserApi.updateUser(me!.id, values);
    if (!result.isSuccessful) {
      errorNotification({ message: result.message });
      personalDataForm.setFieldValue("email", me!.email);
    } else {
      successNotification({ message: result.message });
      updateMe(values);
    }
  };

  const setNewPassword = async (values: typeof passwordForm.values) => {
    const data = {
      currentPassword: values.currentPassword,
      newPassword: values.newPassword,
    };
    const res = await UserApi.updatePassword(data);
    if (res.isSuccessful) {
      successNotification({
        message: "Lozinka uspješno promijenjena",
      });
      passwordForm.reset();
    } else {
      errorNotification({
        message: res?.message ?? "Molimo pokušajte ponovno.",
      });
    }
  };

  const deleteMyAccount = async () => {
    close();
    if (me) {
      const res = await UserApi.deleteUser(me.id);
      if (res?.isSuccessful) {
        await logout();
        navigate("/login");
        successNotification({
          message: "Vaš račun je uspješno obrisan",
        });
      } else {
        errorNotification({
          message: res.message,
        });
      }
    }
  };

  return (
    <div className="profile-main-container">
      {!mobileStep && <div className="profile-title">Profil</div>}
      <div className="profile-desktop-view">
        <PersonalData
          formData={personalDataForm}
          saveMyDetails={saveMyDetails}
          open={open}
        />
        <Password formData={passwordForm} setNewPassword={setNewPassword} />
        <Subscription
          activeSubscription={activeSubscription}
          subscriptionData={subscriptionData}
        />
        <History orders={orders} />
      </div>
      <MobileView
        mobileStep={mobileStep}
        setMobileStep={setMobileStep}
        activeSubscription={activeSubscription}
        subscriptionData={subscriptionData}
        orders={orders}
        personalDataForm={personalDataForm}
        saveMyDetails={saveMyDetails}
        passwordForm={passwordForm}
        setNewPassword={setNewPassword}
        logoutHandler={logoutHandler}
        open={open}
      />
      <AreYouSureModal
        opened={opened}
        message="Jeste li sigurni da želite obrisati korisnički račun? Više ga nećete moći koristiti!"
        onClose={close}
        onAction={deleteMyAccount}
      />
    </div>
  );
};

export default Profile;
